import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import TrendCard from "components/common/TrendCard";
import * as labels from "config.labels";
import { useDispatch, useSelector } from "react-redux";
import * as exposureActions from "store/actions/input/exposure/exposureActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";

const BlankChart = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return (
    <Stack
      sx={{
        width: "100%",
        height: "150px",
        borderRadius: "0.5rem",
        border: "1px solid gainsboro",
      }}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Typography
        padding={8}
        textAlign={"center"}
        variant={"body2"}
        color={"silver !important"}
      >
        {translateLabel(labels.PRIMARY_EXPOSURE_BLANK_CHART_MESSAGE, {
          default: "Please enter one or more data points",
        })}
      </Typography>
    </Stack>
  );
};

const PrimaryExposure = ({
  hideInput,
  hideChart,
  fullWidth,
  borderColour,
  ...componentProps
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const exposures = useSelector(exposureSelectors.getExposureList);
  const data = useSelector(exposureSelectors.getData);
  const primaryExposure = useSelector(exposureSelectors.getPrimaryExposure);
  const dispatch = useDispatch();

  const selectedExposure = (exposures ?? []).filter(
    (x) => x.key === primaryExposure
  )?.[0];

  let trendData = {
    name: selectedExposure?.name,
    values: [],
  };

  let yearLabels = [];
  let yearRangeLabel;

  if (selectedExposure != null && data != null) {
    const years = Object.keys(data).sort((a, b) => a - b);
    years.forEach((year) => {
      const metric = Object.entries(data[year]).filter(
        ([k, _]) => k === selectedExposure.key
      )?.[0]?.[1];
      if (metric != null) {
        yearLabels.push(year);
        trendData.values.push(metric);
      }
    });
    const enDashChar = "\u2013";
    yearRangeLabel = `${yearLabels[0]}${enDashChar}${
      yearLabels[yearLabels.length - 1]
    }`;
  }

  return (
    <Component
      title={
        hideInput
          ? null
          : translateLabel(labels.PRIMARY_EXPOSURE_TITLE, {
              default: "Primary Exposure",
            })
      }
      subtitle={
        hideInput
          ? null
          : translateLabel(labels.PRIMARY_EXPOSURE_SUBTITLE, {
              default: "The main exposure metric driving risk and price.",
            })
      }
      {...componentProps}
    >
      {!hideInput && (
        <FormControl variant={"filled"} fullWidth>
          <InputLabel shrink>
            {translateLabel(labels.PRIMARY_EXPOSURE_LABEL, {
              default: "Primary Exposure",
            })}
          </InputLabel>
          <Select
            value={primaryExposure ?? ""}
            onChange={(e) => {
              dispatch(exposureActions.updatePrimaryExposure(e.target.value));
            }}
          >
            {(exposures ?? []).map((exposure) => (
              <MenuItem key={exposure.key} value={exposure.key}>
                {exposure.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {!hideChart && (
        <Stack marginTop={hideInput ? 0 : 2}>
          {trendData.values.length > 1 && (
            <TrendCard
              data={trendData}
              labels={yearLabels}
              borderColour={borderColour}
              secondaryLabel={yearRangeLabel}
              precentLabelAddon={" YoY"}
              showTooltip
              fullWidth={fullWidth}
            />
          )}
          {trendData.values.length === 1 && primaryExposure != null && (
            <Stack marginLeft={1.5}>
              <Typography variant={"subtitle2"}>
                {selectedExposure?.name}
              </Typography>
              <Typography variant={"h6"}>
                {(trendData.values[0] ?? 0).toLocaleString("en-US")}
              </Typography>
            </Stack>
          )}
          {trendData.values.length < 1 && primaryExposure != null && (
            <BlankChart />
          )}
        </Stack>
      )}
    </Component>
  );
};

export default PrimaryExposure;
